import { t } from "i18next";

import { Box, SxProps, Typography } from "@mui/material";

import { theme } from "@/src/theme/theme";

import { DealerAuctionDTO } from "@/src/types/types";
import { addThousandsSeparator } from "@/src/utils/numberUtils";

interface OurBidInfoProps {
  auction: DealerAuctionDTO;
  align?: "left" | "center";
  showUserInfo?: boolean;
  sx?: SxProps;
}

const OurBidInfo = (props: OurBidInfoProps) => {
  const { align = "center", showUserInfo = false } = props;
  const highestBidAmount = props.auction?.highestBid
    ? props.auction.highestBid.amount
    : 0;

  const myHighestBidAmount = props.auction?.myHighestBid
    ? props.auction.myHighestBid.amount
    : 0;

  const ourHighestBidAmount = props.auction?.ourHighestBid
    ? props.auction.ourHighestBid.amount
    : 0;

  // If (also) other buyers in our company have added bid machines, the winning
  // bid machine must be the one that holds the current ourHighestBid.
  const ourTopBidMachineId = props.auction.ourHighestBid?.bidMachineId;
  const ourHighestBidMachine =
    ourTopBidMachineId && props.auction.ourBidMachines
      ? props.auction.ourBidMachines.find(
          (machine) => machine.id === ourTopBidMachineId
        )
      : undefined;
  const ourHighestBidMachineMaxAmount =
    ourHighestBidMachine && ourHighestBidMachine.maxAmount > 0
      ? ourHighestBidMachine.maxAmount
      : 0;

  if (ourHighestBidAmount <= 0 || myHighestBidAmount >= ourHighestBidAmount) {
    return null;
  }

  const ourBidColorStyle =
    ourHighestBidAmount >= highestBidAmount
      ? { color: theme.palette.semantic.success.main }
      : { color: theme.palette.semantic.warning.main };

  let userInfo = "";
  const user = props.auction?.ourHighestBid?.user;
  if (showUserInfo && user) {
    if (user.firstName && user.firstName !== "") {
      userInfo = user.firstName;
    }
    if (user.lastName && user.lastName !== "") {
      userInfo = `${userInfo} ${user.lastName}`;
    }
    if (user.businessName && user.businessName !== "") {
      userInfo = `${userInfo} (${user.businessName})`;
    }
  }

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: align,
        ...props.sx
      }}
    >
      <Typography variant="caption" sx={ourBidColorStyle}>
        {t("components.ourBidInfo.bidFromOurCompany", {
          highestBid: addThousandsSeparator(ourHighestBidAmount)
        })}
      </Typography>
      {ourHighestBidMachineMaxAmount > highestBidAmount && (
        <Typography variant="caption" sx={ourBidColorStyle}>
          {t("components.ourBidInfo.bidMachineFromOurCompany", {
            maxAmount: addThousandsSeparator(ourHighestBidMachineMaxAmount)
          })}
        </Typography>
      )}
      {userInfo !== "" && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          {userInfo}
        </Typography>
      )}
    </Box>
  );
};

export default OurBidInfo;
